<template>
  <div class="card">
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-xl font-bold">Upload History</h3>
      <Button
        label="Refresh"
        icon="pi pi-refresh"
        variant="outlined"
        size="small"
        @click="handleRefresh"
        :loading="loading"
        data-test-id="history-refresh-btn"
      />
    </div>

    <DataTable
      :value="loading ? skeletonData : injectors"
      :paginator="!loading && totalElements > 0"
      lazy
      :filterDisplay="isAdminMode ? 'row' : undefined"
      v-model:filters="tableFilters"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      currentPageReportTemplate="Showing {first} to {last} of {totalRecords} items"
      :rows="pageSize"
      :first="currentPage * pageSize"
      :totalRecords="totalElements"
      :rowsPerPageOptions="[10, 20, 50]"
      class="p-datatable-sm"
      stripedRows
      responsiveLayout="scroll"
      scrollable
      scrollHeight="400px"
      sortField="CreatedAt"
      :sortOrder="-1"
      @row-click="onRowClick"
      @page="onPage"
      @sort="onSort"
      @filter="onTableFilter"
      rowHover
      data-test-id="tax-injector-history-table"
    >
      <!-- Empty state -->
      <template #empty>
        <div
          class="flex flex-col items-center justify-center py-12 text-surface-500 dark:text-surface-400"
          data-test-id="history-table-empty"
        >
          <i class="pi pi-inbox text-4xl mb-3"></i>
          <p class="text-lg font-medium">No upload history found</p>
          <p class="text-sm">
            Upload a CSV file to see your injection history here.
          </p>
        </div>
      </template>

      <!-- File Upload ID Column -->
      <Column
        field="ExternalId"
        header="File Upload ID"
        style="min-width: 20rem"
      >
        <template #body="{ data }">
          <div v-if="loading">
            <Skeleton width="16rem" height="1rem"></Skeleton>
          </div>
          <span
            v-else
            class="font-mono text-sm text-rch-black dark:text-surface-300 hover:text-surface-800 dark:hover:text-surface-200 underline underline-offset-4 cursor-pointer"
          >
            {{ data.ExternalId }}
          </span>
        </template>
      </Column>

      <!-- Filename Column -->
      <Column
        field="Filename"
        header="Filename"
        style="min-width: 15rem"
      >
        <template #body="{ data }">
          <div v-if="loading">
            <Skeleton width="12rem" height="1rem"></Skeleton>
          </div>
          <div v-else-if="data.Filename" class="flex items-center gap-2">
            <i class="pi pi-file text-surface-500 dark:text-surface-400 text-sm"></i>
            <span 
              class="text-sm text-surface-700 dark:text-surface-200 truncate" 
              :title="data.Filename"
              style="max-width: 12rem"
            >
              {{ data.Filename }}
            </span>
          </div>
          <span v-else class="text-sm text-surface-400 dark:text-surface-500">—</span>
        </template>
      </Column>

      <!-- Created By Column (admin mode only) -->
      <Column
        v-if="isAdminMode"
        field="CreatedBy"
        header="Created By"
        :filter="isAdminMode"
        filterField="CreatedBy"
        :showFilterMenu="false"
        :showFilterMatchModes="false"
        :showFilterOperator="false"
        style="min-width: 14rem"
      >
        <template #body="{ data }">
          <div v-if="loading"><Skeleton width="10rem" height="1rem" /></div>
          <span v-else class="text-sm text-surface-700 dark:text-surface-200">{{ data.CreatedBy }}</span>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <div class="flex gap-1">
            <InputText
              v-model="filterModel.value"
              type="text"
              placeholder="Filter by email..."
              class="p-column-filter"
              size="small"
              data-test-id="created-by-filter"
              @input="filterCallback()"
            />
            <Button
              icon="pi pi-times"
              variant="outlined"
              size="small"
              severity="secondary"
              :disabled="!filterModel.value"
              @click="clearEmailFilter(filterModel, filterCallback)"
              data-test-id="created-by-filter-clear-btn"
            />
          </div>
        </template>
      </Column>

      <!-- Created At Column -->
      <Column
        field="CreatedAt"
        header="Created At"
        style="min-width: 12rem"
        :sortable="true"
      >
        <template #body="{ data }">
          <div v-if="loading">
            <Skeleton width="10rem" height="1rem"></Skeleton>
          </div>
          <span v-else class="text-sm text-surface-700 dark:text-surface-200">
            {{ formatDateTimeForDisplay(data.CreatedAt) }}
          </span>
        </template>
      </Column>

      <!-- Actions Column -->
      <Column header="Actions" style="min-width: 8rem">
        <template #body="{ data }">
          <div v-if="loading">
            <Skeleton width="4rem" height="2rem"></Skeleton>
          </div>
          <div v-else class="flex gap-2">
            <Button
              icon="pi pi-download"
              variant="outlined"
              size="small"
              severity="secondary"
              @click.stop="onDownloadFile(data)"
              :disabled="!data.Filename"
              :loading="downloadingIds.has(data.ExternalId)"
              v-tooltip.top="data.Filename ? 'Download original file' : 'No file available'"
              :aria-label="`Download file for ${data.ExternalId}`"
              data-test-id="download-file-btn"
            />
          </div>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Skeleton from 'primevue/skeleton'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import { FilterMatchMode } from '@primevue/core/api'
import { formatDateTimeForDisplay } from '@/util/dateUtils'
import type { TaxInjectorSummary } from '@/types/taxInjector'

interface Props {
  injectors: TaxInjectorSummary[]
  loading: boolean
  isAdminMode?: boolean
  totalElements?: number
  currentPage?: number
  pageSize?: number
}

const props = withDefaults(defineProps<Props>(), {
  isAdminMode: false,
  totalElements: 0,
  currentPage: 0,
  pageSize: 20,
})

const emit = defineEmits<{
  'row-select': [injector: TaxInjectorSummary]
  'refresh': []
  'download-file': [injector: TaxInjectorSummary]
  'filter-change': [email: string]
  'page-change': [event: { page: number; rows: number }]
  'sort-change': [sort: string]
}>()

// Track downloading state per ExternalId
const downloadingIds = ref<Set<string>>(new Set())

// Skeleton data for loading state
const skeletonData = computed(() => Array(5).fill({}))

// PrimeVue lazy filter state
const tableFilters = ref({
  CreatedBy: { value: null as string | null, matchMode: FilterMatchMode.CONTAINS },
})

function onRowClick(event: { data: TaxInjectorSummary }) {
  if (!props.loading) {
    emit('row-select', event.data)
  }
}

function handleRefresh() {
  emit('refresh')
}

function onDownloadFile(injector: TaxInjectorSummary) {
  if (!props.loading && injector.Filename) {
    downloadingIds.value.add(injector.ExternalId)
    emit('download-file', injector)
  }
}

function onPage(event: { page: number; rows: number }) {
  emit('page-change', event)
}

function onTableFilter() {
  emit('filter-change', tableFilters.value.CreatedBy.value || '')
}

function clearEmailFilter(filterModel: { value: string | null }, filterCallback: () => void) {
  filterModel.value = null
  filterCallback()
}

// Map PrimeVue DataTable field names to Spring Pageable sort fields
const SORT_FIELD_MAP: Record<string, string> = {
  CreatedAt: 'audit.createdOn',
}

function onSort(event: { sortField: string; sortOrder: number }) {
  const backendField = SORT_FIELD_MAP[event.sortField] ?? event.sortField
  const direction = event.sortOrder === 1 ? 'asc' : 'desc'
  emit('sort-change', `${backendField},${direction}`)
}

// Function to clear downloading state (called from parent after download completes)
function clearDownloadingState(externalId: string) {
  downloadingIds.value.delete(externalId)
}

// Expose the function so parent can call it
defineExpose({
  clearDownloadingState,
})
</script>

